// Home Power Score model — customer-facing name for Powlo's real Powtential™
// scoring engine. Six weighted pillars, 0-100 each, blended into one overall
// score. Weights and band cut-points below are the real production values
// (confirmed against a live customer Energy Plan PDF), not illustrative ones.
//
// "Generation Potential" (the roof) and "Property Performance" (the
// building/EPC) are physical property facts — they don't move when you
// change the recommended system, only if the building itself changes.
// The other four pillars respond to the system, tariff and usage.

export interface Pillars {
  generation: number; // "Your roof" — Generation Potential — fixed property fact
  economic: number; // "The money" — Economic Opportunity
  independence: number; // "Off the grid" — Energy Independence
  property: number; // "The building" — Property Performance — fixed property fact
  smartReady: number; // "Smart ready" — Smart Energy Readiness
  systemFit: number; // "System fit" — Optimisation Potential
}

export const FIXED_PILLARS: (keyof Pillars)[] = ["generation", "property"];

export const WEIGHTS: Record<keyof Pillars, number> = {
  generation: 0.25,
  economic: 0.25,
  independence: 0.2,
  property: 0.1,
  smartReady: 0.1,
  systemFit: 0.1,
};

export const PILLAR_LABELS: Record<keyof Pillars, string> = {
  generation: "Generation Potential",
  economic: "Economic Opportunity",
  independence: "Energy Independence",
  property: "Property Performance",
  smartReady: "Smart Energy Readiness",
  systemFit: "Optimisation Potential",
};

// The colloquial names used alongside the formal ones on the plan/score UI.
export const PILLAR_NICKNAMES: Record<keyof Pillars, string> = {
  generation: "Your roof",
  economic: "The money",
  independence: "Off the grid",
  property: "The building",
  smartReady: "Smart ready",
  systemFit: "System fit",
};

export const PILLAR_DETAIL: Record<keyof Pillars, string> = {
  generation:
    "How much electricity your roof can actually make. It comes down to how big it is, which way it faces, how steep it is, and what is casting shade on it. Nothing you fit changes this one — it is a fact about the building.",
  economic:
    "Whether the money stacks up. We weigh what the system costs against what it saves you each year, and how quickly that pays you back. A cheap overnight tariff and a battery sized to your evenings both push this up.",
  independence:
    "How much electricity you stop buying from the grid. Panels cover the daytime, a battery covers the evening. Add an electric car or a heat pump and you use more, so the same system covers a smaller share of it.",
  property:
    "What your home does with the energy once it is inside. This is your EPC rating and how far this system would lift it — which also feeds into what the house is worth.",
  smartReady:
    "Whether your setup can react to price. A time-of-use tariff plus a battery lets you buy power when it is cheap and use it when it is expensive. Without a cheap window there is nothing to play against.",
  systemFit:
    "How well the design fits the house — enough of the roof used, a battery sized to what you really use in the evening, and a tariff doing some work. Fitting more of what the roof can carry pushes this up.",
};

export function overallScore(p: Pillars): number {
  const total = (Object.keys(WEIGHTS) as (keyof Pillars)[]).reduce(
    (sum, k) => sum + p[k] * WEIGHTS[k],
    0
  );
  return Math.round(total);
}

// Real production score bands. textColor is the readable label colour for
// a FILLED pill in that band's colour — Strong/Excellent are bright
// lime/yellow-green, so they need dark text (same convention as the lime
// "Design my system" CTA); the darker purple/pink bands read fine in white.
export function bandFor(score: number): { label: string; color: string; textColor: string } {
  if (score >= 80) return { label: "Excellent", color: "#D4FF00", textColor: "#0C0D10" };
  if (score >= 70) return { label: "Strong", color: "#B4EC3F", textColor: "#0C0D10" };
  if (score >= 58) return { label: "Good", color: "#F06AD8", textColor: "#FFFFFF" };
  if (score >= 45) return { label: "Fair", color: "#B063F7", textColor: "#FFFFFF" };
  return { label: "Limited", color: "#7D6EFF", textColor: "#FFFFFF" };
}

export const BAND_TABLE = [
  { range: "0–44", label: "Limited", color: "#7D6EFF" },
  { range: "45–57", label: "Fair", color: "#B063F7" },
  { range: "58–69", label: "Good", color: "#F06AD8" },
  { range: "70–79", label: "Strong", color: "#B4EC3F" },
  { range: "80–100", label: "Excellent", color: "#D4FF00" },
];

// Baseline: property as it stands, nothing fitted. Overall ≈ 34 (Limited on
// the real band table above — the climb from here is what the plan is for).
export const BASELINE: Pillars = {
  generation: 58,
  economic: 19,
  independence: 22,
  property: 55,
  smartReady: 30,
  systemFit: 18,
};

// AI-recommended "best setup": panels sized to the roof + a battery matched
// to evening load + a tariff switch. Overall ≈ 73 (Strong) — the reference
// point from the real example plan.
export const BEST_SETUP: Pillars = {
  generation: 58,
  economic: 81,
  independence: 82,
  property: 55,
  smartReady: 85,
  systemFit: 78,
};

export interface Lever {
  key: string;
  label: string;
  description: string;
  affects: (keyof Pillars)[];
  delta: Partial<Pillars>;
}

export const LEVERS: Lever[] = [
  {
    key: "panels",
    label: "Add more solar panels",
    description: "More generation capacity put to work.",
    affects: ["economic", "independence"],
    delta: { economic: 22, independence: 10 },
  },
  {
    key: "battery",
    label: "Add a home battery",
    description: "Store surplus solar and shift usage to cheap hours.",
    affects: ["independence", "systemFit", "economic"],
    delta: { independence: 20, systemFit: 45, economic: 12 },
  },
  {
    key: "tariff",
    label: "Switch to a time-of-use tariff",
    description: "Buy cheap, sell high, automatically.",
    affects: ["smartReady"],
    delta: { smartReady: 40 },
  },
  {
    key: "ev",
    label: "Plan for an EV",
    description: "Charge overnight on cheap rates.",
    affects: ["economic", "smartReady"],
    delta: { economic: 18, smartReady: 8 },
  },
];

export function clampPillar(v: number): number {
  return Math.max(0, Math.min(100, Math.round(v)));
}

export function applyLevers(base: Pillars, activeKeys: string[]): Pillars {
  const result: Pillars = { ...base };
  for (const key of activeKeys) {
    const lever = LEVERS.find((l) => l.key === key);
    if (!lever) continue;
    for (const k of Object.keys(lever.delta) as (keyof Pillars)[]) {
      result[k] = clampPillar(result[k] + (lever.delta[k] ?? 0));
    }
  }
  return result;
}
